import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { usePredictionsInfoContext } from '@futrli/predict/forecast/detail/context/predictions-info-context';
import { useRouteIds } from '@futrli/predict/hooks/useRouteIds';

interface LockForecastDialogProps {
  forecastId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const LockForecastDialog = ({ forecastId, isOpen, onClose }: LockForecastDialogProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();
  const { syncForecast } = usePredictionsInfoContext();

  const view = useApiView(
    useMemo(() => new ModifyForecastView(workspaceId, organisationId, forecastId), [
      workspaceId,
      organisationId,
      forecastId
    ])
  );

  const handleLock = useCallback(() => {
    void view
      .lockForecast()
      .then(updatedForecast => {
        if (updatedForecast && syncForecast) {
          syncForecast(updatedForecast);
        }
      })
      .catch(() => undefined);
  }, [syncForecast, view]);

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isUpdating,
    error: view.state.errorUpdating,
    onAction: handleLock,
    onClose
  });

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleAction}
      screen={screen}
      title={intl.formatMessage({ id: 'predict.forecasting.lock', defaultMessage: 'Lock' })}
      subtitle={view.state.forecast?.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={intl.formatMessage({
        id: 'predict.forecasting.lock_forecast_description',
        defaultMessage: 'You will no longer be able to edit this forecast when locked'
      })}
      loadingMessage={intl.formatMessage({
        id: 'predict.forecasting.locking_forecast',
        defaultMessage: 'Locking forecast...'
      })}
      successMessage={intl.formatMessage({
        id: 'predict.forecasting.forecast_locked',
        defaultMessage: 'Forecast locked'
      })}
      errorMessage={view.state.errorUpdating?.message}
      dataTestId="lock-forecast-dialog"
    />
  );
};
